// @ts-nocheck
/* eslint-disable react/no-is-mounted */
import React, { useCallback, useEffect, useRef, useState } from "react";
import DateContentRow from "react-big-calendar/lib/DateContentRow";
import DateHeader from "react-big-calendar/lib/DateHeader";
import Header from "react-big-calendar/lib/Header";
import Popup from "react-big-calendar/lib/Popup";
import { navigate, views } from "react-big-calendar/lib/utils/constants";
import * as dates from "react-big-calendar/lib/utils/dates";
import { notify } from "react-big-calendar/lib/utils/helpers";
import Overlay from "react-overlays/Overlay";
import clsx from "clsx";
import * as animationFrame from "dom-helpers/animationFrame";
import getPosition from "dom-helpers/position";
import chunk from "lodash/chunk";
import { eventsForWeek, isToday, sortEvents } from "oc/utils";
import PropTypes from "prop-types";

export const MyLeaveCalendarWeeklyTimelineView = (props) => {
  const {
    date,
    localizer,
    className,
    events: allEvents,
    accessors,
    popup,
    selected,
    selectable,
    components,
    getNow,
    getters,
    longPressThreshold,
    rtl,
    onDrillDown,
    onSelectEvent,
    onDoubleClickEvent,
    onSelectSlot,
    onShowMore,
    getDrilldownView,
    popupOffset,
    handleDragStart,
  } = props;

  const [rowLimit, setRowLimit] = useState(5);
  const [needLimitMeasure, setNeedLimitMeasure] = useState(true);
  const [overlay, setOverlay] = useState(null);
  const slotRowRef = useRef();
  const pendingSelection = useRef([]);
  const containerRef = useRef(null);

  const measureRowLimit = useCallback(() => {
    if (slotRowRef.current) {
      setRowLimit(slotRowRef.current.getRowLimit());
      setNeedLimitMeasure(false);
    }
  }, []);

  useEffect(() => {
    if (needLimitMeasure) {
      measureRowLimit();
    }
  }, [needLimitMeasure, measureRowLimit]);

  useEffect(() => {
    let running;
    const handleResize = () => {
      if (!running) {
        animationFrame.request(() => {
          running = false;
          setNeedLimitMeasure(true);
        });
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getContainer = useCallback(() => containerRef.current, []);

  let month = dates.visibleDays(date, localizer);
  let firstOfWeek = localizer.startOfWeek();
  let start = dates.startOf(date, "week", firstOfWeek);
  let end = dates.endOf(date, "week", firstOfWeek);
  let range = dates.range(start, end);
  month = range;
  let weeks = chunk(month, month.length);

  let events = eventsForWeek(
    allEvents,
    month[0],
    month[month.length - 1],
    accessors,
  );
  events.sort((a, b) => (a?.start > b?.start ? 1 : -1));

  const readerDateHeading = ({ date, className, key, ...rest }) => {
    const isOffRange = dates.month(date) !== dates.month(props.date);
    const isCurrent = dates.eq(date, props.date, "day");
    const drilldownView = getDrilldownView(date);
    const label = localizer.format(date, "dateFormat");
    const DateHeaderComponent = components.dateHeader || DateHeader;

    return (
      <div
        key={key}
        {...rest}
        className={clsx(
          className,
          isOffRange && "rbc-off-range",
          isCurrent && "rbc-current",
        )}
      >
        <DateHeaderComponent
          label={label}
          date={date}
          drilldownView={drilldownView}
          isOffRange={isOffRange}
          onDrillDown={(e) => handleHeadingClick(date, drilldownView, e)}
        />
      </div>
    );
  };

  const renderMonth = (month, monthIdx) => {
    let filteredEvents = eventsForWeek(
      allEvents,
      month[0],
      month[month.length - 1],
      accessors,
    );

    filteredEvents.sort((a, b) => sortEvents(a, b, accessors));

    return (
      <DateContentRow
        key={monthIdx}
        ref={monthIdx === 0 ? slotRowRef : undefined}
        container={getContainer}
        className="rbc-month-row"
        getNow={getNow}
        date={date}
        range={month}
        events={filteredEvents}
        maxRows={rowLimit}
        selected={selected}
        selectable={selectable}
        components={components}
        accessors={accessors}
        getters={getters}
        localizer={localizer}
        renderHeader={readerDateHeading}
        renderForMeasure={needLimitMeasure}
        onShowMore={handleShowMore}
        onSelect={handleSelectEvent}
        onDoubleClick={handleDoubleClickEvent}
        onSelectSlot={handleSelectSlot}
        longPressThreshold={longPressThreshold}
        rtl={rtl}
      />
    );
  };

  const handleHeadingClick = (date, view, e) => {
    e.preventDefault();
    clearSelection();
    notify(onDrillDown, [date, view]);
  };

  const handleSelectEvent = (...args) => {
    clearSelection();
    notify(onSelectEvent, args);
  };

  const handleDoubleClickEvent = (...args) => {
    clearSelection();
    notify(onDoubleClickEvent, args);
  };

  const handleShowMore = (events, date, cell, slot, target) => {
    clearSelection();
    if (popup) {
      let position = getPosition(cell, getContainer());
      setOverlay({ date, events, position, target });
    } else {
      notify(onDrillDown, [date, getDrilldownView(date) || views.DAY]);
    }
    notify(onShowMore, [events, date, slot]);
  };

  const handleSelectSlot = (range, slotInfo) => {
    pendingSelection.current = pendingSelection.current.concat(range);
    clearTimeout(pendingSelection.current._selectTimer);
    pendingSelection.current._selectTimer = setTimeout(() => {
      const slots = [...pendingSelection.current].sort((a, b) => +a - +b);
      pendingSelection.current = [];
      notify(onSelectSlot, {
        slots,
        start: slots[0],
        end: slots[slots.length - 1],
        action: slotInfo.action,
        bounds: slotInfo.bounds,
        box: slotInfo.box,
      });
    });
  };

  const clearSelection = () => {
    clearTimeout(pendingSelection.current._selectTimer);
    pendingSelection.current = [];
  };

  const renderHeaders = (row, date) => {
    const first = row[0];
    const last = row[row.length - 1];
    const HeaderComponent = components.header || Header;

    return dates.range(first, last, "day").map((day, idx) => (
      <div
        key={"header_" + idx}
        className={clsx(
          "rbc-header",
          isToday(day) && "rbc-today",
          dates.month(date) !== dates.month(day) && "rbc-off-range-bg",
        )}
      >
        <HeaderComponent
          date={day}
          localizer={localizer}
          label={localizer.format(day, "weekdayFormat")}
        />
      </div>
    ));
  };

  return (
    <div ref={containerRef} className={clsx("rbc-month-view", className)}>
      <div className="rbc-row rbc-month-header">
        {renderHeaders(month, date)}
      </div>
      {weeks.map(renderMonth)}
      {popup && overlay && (
        <Overlay
          rootClose
          placement="bottom"
          show={!!overlay.position}
          onHide={() => setOverlay(null)}
          target={() => overlay.target}
        >
          {({ props: overlayProps }) => (
            <Popup
              {...overlayProps}
              popupOffset={popupOffset}
              accessors={accessors}
              getters={getters}
              selected={selected}
              components={components}
              localizer={localizer}
              position={overlay.position}
              show={() => setOverlay(null)}
              events={overlay.events}
              slotStart={overlay.date}
              slotEnd={overlay.end}
              onSelect={handleSelectEvent}
              onDoubleClick={handleDoubleClickEvent}
              handleDragStart={handleDragStart}
            />
          )}
        </Overlay>
      )}
    </div>
  );
};

MyLeaveCalendarWeeklyTimelineView.propTypes = {
  events: PropTypes.array.isRequired,
  date: PropTypes.instanceOf(Date),
  min: PropTypes.instanceOf(Date),
  max: PropTypes.instanceOf(Date),
  step: PropTypes.number,
  getNow: PropTypes.func.isRequired,
  scrollToTime: PropTypes.instanceOf(Date),
  rtl: PropTypes.bool,
  width: PropTypes.number,
  accessors: PropTypes.object.isRequired,
  components: PropTypes.object.isRequired,
  getters: PropTypes.object.isRequired,
  localizer: PropTypes.object.isRequired,
  selected: PropTypes.object,
  selectable: PropTypes.oneOf([true, false, "ignoreEvents"]),
  longPressThreshold: PropTypes.number,
  onNavigate: PropTypes.func,
  onSelectSlot: PropTypes.func,
  onSelectEvent: PropTypes.func,
  onDoubleClickEvent: PropTypes.func,
  onShowMore: PropTypes.func,
  onDrillDown: PropTypes.func,
  getDrilldownView: PropTypes.func.isRequired,
  popup: PropTypes.bool,
  handleDragStart: PropTypes.func,
  popupOffset: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
  ]),
};

MyLeaveCalendarWeeklyTimelineView.range = (date, { localizer }) => {
  const start = dates.firstVisibleDay(date, localizer);
  const end = dates.lastVisibleDay(date, localizer);
  return { start, end };
};

MyLeaveCalendarWeeklyTimelineView.navigate = (date, action) => {
  switch (action) {
    case navigate.PREVIOUS:
      return dates.add(date, -1, "week");
    case navigate.NEXT:
      return dates.add(date, 1, "week");
    default:
      return date;
  }
};

MyLeaveCalendarWeeklyTimelineView.title = (date, { localizer }) =>
  localizer.format(date, "monthHeaderFormat");

export default MyLeaveCalendarWeeklyTimelineView;
